import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {ObjectNode} from "#template/nodes/object/object-node"
import {z} from "zod/v4-mini"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {ObjectLikeNode, UnitForDisplay} from "#template/nodes/types"
import {unitForDisplay} from "#template/nodes/validation"
import {objectLike} from "#template/nodes/object/fundamental"
import {boundsEdge, getBoundsEdge} from "#template/utils/scene-geometry-utils"
import type {INodeSceneInteractionsContext, TemplateNodePart} from "#template/interactions"
import {Matrix4, Vector3} from "@cm/math"
import type {SceneInteractiveNode} from "#template/role-markers"
import type {DimensionGuideEntry, DimensionGuideInfo, SceneNodes} from "#template/interfaces/scene-object"
import {getBoundsEdgeTransform, indexToDirection} from "#template/utils/dimension-guide-utils"
import {isNodeWithMatrixArrayLockedTransformParameter} from "#template/utils/transform-utils"
import type {BoundsEdgeNumber} from "#template/utils/scene-geometry-utils"
import {visitNone} from "#template/template-node/visit-helpers"

export type DimensionGuidesParameters = {
    name: string
    offset: number
    parent: ObjectLikeNode | null
    guides: [GuideParameters, GuideParameters, GuideParameters]
    unitForDisplay: UnitForDisplay
    concealable: boolean
} & ObjectNodeParameters

export type DimensionGuidesConstructorParameters = Omit<DimensionGuidesParameters, "unitForDisplay" | "concealable" | "visible"> & {
    unitForDisplay?: UnitForDisplay
    concealable?: boolean
    visible?: boolean
}

const guideParameters = z.object({
    boundsEdge,
    show: z.boolean(),
})

export type GuideParameters = z.infer<typeof guideParameters>

const dimensionGuidesDefinition: ObjectNodeDefinition<DimensionGuidesParameters, DimensionGuidesConstructorParameters> = {
    nodeClass: "DimensionGuides",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                offset: z.number(),
                parent: z.nullable(objectLike),
                guides: z.tuple([guideParameters, guideParameters, guideParameters]),
                unitForDisplay: z._default(unitForDisplay, "cm"),
                concealable: z._default(z.boolean(), false),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class DimensionGuides extends ObjectNode<DimensionGuidesParameters, DimensionGuidesConstructorParameters> implements SceneInteractiveNode {
    public override get definition() {
        return dimensionGuidesDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<DimensionGuidesParameters> {
        if (this.parameters.parent === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<DimensionGuidesParameters> {
        const {evaluator} = context
        if (!evaluator.templateContext.showDimensions) return skipped
        const {logger} = evaluator.templateContext

        const {parent, guides, offset, unitForDisplay, concealable} = this.parameters

        const scope = evaluator.getScope(this)

        if (!parent) return skipped

        const [objectData, objectDataInvalid] = scope.branch(evaluator.evaluate(parent))

        const dimensionGuideRawData = scope.tuple(objectData, guides, offset, unitForDisplay)

        const dimensionGuides = scope.pureLambda(
            // pure-lambda-allow: logger
            dimensionGuideRawData,
            ([objectData, guides, offset, unitForDisplay]) => {
                if (!objectData.dimensionGuideBounds) {
                    logger.error("Dimension guides attached to object without bounds")
                    return []
                }

                const {dimensionGuideBounds} = objectData

                const boundsMatrix = Matrix4.fromArray(dimensionGuideBounds.matrix)

                const result: DimensionGuideInfo[] = [...guides.entries()]
                    .filter(([_, guide]) => guide.show)
                    .map(([originalIndex, guide]): DimensionGuideInfo => {
                        const getDimensionGuideEntry = (boundsEdgeNumber: BoundsEdgeNumber): DimensionGuideEntry => {
                            const [s, e] = getBoundsEdge(indexToDirection(originalIndex), boundsEdgeNumber, dimensionGuideBounds)
                            const transform = getBoundsEdgeTransform(indexToDirection(originalIndex), boundsEdgeNumber, offset)

                            const start = Vector3.fromArray(transform.multiplyVectorXYZW(s[0], s[1], s[2], 1))
                            const end = Vector3.fromArray(transform.multiplyVectorXYZW(e[0], e[1], e[2], 1))
                            const startTransformed = Vector3.fromArray(boundsMatrix.multiplyVectorXYZW(start.x, start.y, start.z, 1))
                            const endTransformed = Vector3.fromArray(boundsMatrix.multiplyVectorXYZW(end.x, end.y, end.z, 1))
                            return {
                                start: startTransformed,
                                end: endTransformed,
                                label: convertLength(start.sub(end).norm(), unitForDisplay),
                            }
                        }

                        if (guide.boundsEdge === "auto")
                            return [0 as const, 1 as const, 2 as const, 3 as const].map((boundsEdge) => getDimensionGuideEntry(boundsEdge))
                        else return getDimensionGuideEntry(guide.boundsEdge)
                    })
                return result
            },
            "bounds",
        )

        this.setupObject(
            scope,
            context,
            "DimensionGuides",
            undefined,
            objectData,
            (objectProps) => {
                return scope.struct<SceneNodes.DimensionGuides>("DimensionGuides", {
                    type: "DimensionGuides",
                    ...objectProps,
                    concealable,
                    guides: dimensionGuides,
                })
            },
            objectDataInvalid,
        )

        return visitNone(this.parameters)
    }

    override canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    override canTransformPart(part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): boolean {
        return part !== "root" && isNodeWithMatrixArrayLockedTransformParameter(this)
    }
}

function convertLength(lengthInCm: number, targetUnit: UnitForDisplay): string {
    const conversionFromCm = {
        mm: 10,
        cm: 1,
        m: 0.01,
        in: 1 / 2.54,
        ft: 1 / 30.48,
    }

    const decimalPlaces = {
        mm: 0,
        cm: 0,
        m: 2,
        in: 1,
        ft: 1,
    }

    const convertedValue = lengthInCm * conversionFromCm[targetUnit]
    return `${convertedValue.toFixed(decimalPlaces[targetUnit])} ${targetUnit}`
}
